"use client";

import { useEffect, useRef } from "react";

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Modal shown when an iOS Safari user tries to enable push notifications
 * but hasn't installed the PWA yet. Explains that push only works in
 * standalone mode and walks through the install steps.
 */
export function InstallRequiredModal({ open, onClose }: Props) {
  const backdropRef = useRef<HTMLDivElement>(null);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={backdropRef}
      onClick={(e) => {
        if (e.target === backdropRef.current) onClose();
      }}
      style={backdropStyle}
    >
      <div style={modalStyle}>
        {/* Header icon */}
        <div style={iconStyle}>
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ color: "#6cf" }}
          >
            <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
            <line x1="12" y1="18" x2="12.01" y2="18" />
          </svg>
        </div>

        <div style={{ fontSize: "1rem", fontWeight: 600, color: "var(--color-text)", marginBottom: 8 }}>
          Install to get notifications
        </div>

        <div style={{ fontSize: "0.82rem", color: "var(--color-text-muted)", lineHeight: 1.5, marginBottom: 20 }}>
          On iPhone, push notifications only work when the app is installed to your home screen.
        </div>

        <div style={stepsContainerStyle}>
          <Step number={1}>
            Tap the share button{" "}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ verticalAlign: "middle", display: "inline-block", color: "#6cf" }}
            >
              <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
              <polyline points="16 6 12 2 8 6" />
              <line x1="12" y1="2" x2="12" y2="15" />
            </svg>{" "}
            in Safari
          </Step>
          <Step number={2}>
            Scroll down and tap <strong>&ldquo;Add to Home Screen&rdquo;</strong>
          </Step>
          <Step number={3}>Open the app from your home screen</Step>
          <Step number={4}>Enable notifications when prompted</Step>
        </div>

        <button onClick={onClose} style={gotItButtonStyle}>
          Got it
        </button>
      </div>
    </div>
  );
}

function Step({ number, children }: { number: number; children: React.ReactNode }) {
  return (
    <div style={stepStyle}>
      <div style={stepNumberStyle}>{number}</div>
      <div style={{ flex: 1, fontSize: "0.82rem", color: "var(--color-text-secondary)", lineHeight: 1.4 }}>
        {children}
      </div>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const backdropStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 2000,
  background: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
};

const modalStyle: React.CSSProperties = {
  background: "var(--color-bg)",
  borderRadius: 16,
  padding: "28px 24px 24px",
  maxWidth: 360,
  width: "100%",
  textAlign: "center",
  boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
};

const iconStyle: React.CSSProperties = {
  width: 52,
  height: 52,
  borderRadius: 14,
  background: "rgba(102, 204, 255, 0.1)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 16px",
};

const stepsContainerStyle: React.CSSProperties = {
  textAlign: "left",
  display: "flex",
  flexDirection: "column",
  gap: 12,
  marginBottom: 24,
};

const stepStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: 10,
};

const stepNumberStyle: React.CSSProperties = {
  width: 22,
  height: 22,
  borderRadius: "50%",
  background: "var(--color-bg-hover)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "0.72rem",
  fontWeight: 600,
  color: "var(--color-text-secondary)",
  flexShrink: 0,
};

const gotItButtonStyle: React.CSSProperties = {
  background: "var(--color-btn-primary-bg)",
  color: "var(--color-btn-primary-text)",
  border: "none",
  borderRadius: 8,
  padding: "10px 24px",
  fontSize: "0.85rem",
  fontWeight: 600,
  cursor: "pointer",
  width: "100%",
};
